import { useMemo, useRef } from 'react';
import cx from 'classnames';
import { GetStaticPropsContext } from 'next';

import {
    BaseDataSelector,
    VEHICLE_CLASSES,
} from '../../components/DataSelector';
import { api, RegionSettings, Settings, VehicleClass } from '../../data/api';
import { RegionChartWrapper } from '../../components/region/RegionChart';
import { SelectItem } from '../../components/Select';
import { useMetricsForMultipleCities } from '../../components/metrics/use-metrics';
import { useQueryState } from '../../utils/use-query-state';
import AboutThisData from '../../components/AboutThisData';
import Layout from '../../components/Layout';
import RegionHeader from '../../components/region/RegionHeader';
import styles from '../../styles/Region.module.scss';
import TableSection from '../../components/TableSection';
import {
    generateTimeRanges,
    TimeRangeListItem,
} from '../../data/available-time-ranges';
import { generateVehicleClasses } from '../../data/available-vehicle-classes';
import RegionTitleBlock from '../../components/region/RegionTitleBlock';
import MetricTable from '../../components/region/MetricTable';

interface RegionProps {
    regionSettings: RegionSettings;
    citySettings: { [slug: string]: Settings };
    validTimeRanges: TimeRangeListItem[];
    validVehicleClasses: VehicleClass[];
}

function Region({
    regionSettings,
    citySettings,
    validTimeRanges,
    validVehicleClasses,
}: RegionProps) {
    const connectedCitiesRef = useRef<HTMLDivElement>(null);
    const [timeRange, setTimeRange] = useQueryState('time', 'all');
    const [vehicleClass, setVehicleClass] = useQueryState('vehicle', 'all');

    const timeRangeItems = Object.fromEntries(
        validTimeRanges.map(({ name, key }) => [key, { name, key }])
    );

    const vehicleClassItems = Object.fromEntries(
        validVehicleClasses.map(vc => [
            vc,
            {
                ...(VEHICLE_CLASSES[vc] as SelectItem),
            },
        ])
    );
    const slugs = useMemo(
        () => regionSettings.city_metadata.map(city => city.slug),
        [regionSettings.city_metadata]
    );
    const metrics = useMetricsForMultipleCities(
        slugs,
        timeRange,
        vehicleClass as VehicleClass,
        null
    );

    return (
        <Layout
            header={<RegionHeader settings={regionSettings} />}
            title={`${regionSettings.name} | Micromobility Dashboard`}
        >
            <div className={styles.pageContainer}>
                <div className={styles.sectionContainer}>
                    <RegionTitleBlock
                        connectedCitiesRef={connectedCitiesRef}
                        citySettings={citySettings}
                        metrics={metrics}
                        regionSettings={regionSettings}
                    />
                </div>
                <div className={cx('shadow-8', styles.selectors)}>
                    <BaseDataSelector
                        timeRange={timeRange}
                        setTimeRange={setTimeRange}
                        vehicleClass={vehicleClass}
                        setVehicleClass={setVehicleClass}
                        vehicleClassItems={vehicleClassItems}
                        timeRangeItems={timeRangeItems}
                    />
                </div>
                <TableSection>
                    <h1 className={cx(styles.h1, styles.title)}>
                        Trip & Vehicle Trends
                    </h1>
                    <RegionChartWrapper
                        metricsMap={metrics}
                        timeRange={timeRange}
                    />
                    <h1
                        className={cx(
                            styles.h1,
                            styles.title,
                            styles.connectedCitiesTitle
                        )}
                        ref={connectedCitiesRef}
                    >
                        Connected Cities
                    </h1>
                    <MetricTable
                        region={regionSettings}
                        citySettings={citySettings}
                        metricsMap={metrics}
                    />
                </TableSection>
                <section>
                    <AboutThisData />
                </section>
            </div>
        </Layout>
    );
}

const validVehicleClasses = async (slugs: string[]) => {
    const vehicleClassSet = new Set<VehicleClass>();

    for (const slug of slugs) {
        const newVehicleClasses = await generateVehicleClasses(slug);
        newVehicleClasses.forEach(vc => vehicleClassSet.add(vc));
    }

    const vehicleClassArray = Array.from(vehicleClassSet);
    // sorting so the user sees a consistent ordering
    vehicleClassArray.sort();

    return vehicleClassArray;
};

const validTimeRanges = async (citySettings: { [slug: string]: Settings }) => {
    const timeRangeSet = new Set<TimeRangeListItem>();

    for (const [slug, settings] of Object.entries(citySettings)) {
        const newTimeRanges = await generateTimeRanges(
            settings.earliest_available_metrics ||
                settings.earliest_available_routes,
            slug,
            'all'
        );
        newTimeRanges.forEach(timeRange => timeRangeSet.add(timeRange));
    }
    const timeRangeArray = Array.from(timeRangeSet);
    // Custom sort logic: all should be first, then descending
    timeRangeArray.sort((a, b) => {
        if (a.key == 'all') {
            return -1;
        }
        if (b.key == 'all') {
            return 1;
        }

        // keys look like 2021-Q3; by reverse sorting here, the select will be in descending order
        if (a.key < b.key) {
            return 1;
        }
        return -1;
    });
    return timeRangeArray;
};

export async function getStaticProps(context: GetStaticPropsContext) {
    const slug = context.params?.slug;
    if (typeof slug !== 'string') {
        throw new Error(`Invalid slug ${slug}`);
    }

    const allRegionSettings = await api.regionSettings(slug);

    const citySettings = {};
    if (allRegionSettings != null) {
        for (let cityMetadata of allRegionSettings.city_metadata) {
            const settings = await api.settings(cityMetadata.slug);
            if (settings != null) {
                citySettings[cityMetadata.slug] = settings;
            }
        }
    }

    const citySlugs = Object.keys(citySettings);

    const regionSettings: RegionSettings | null = allRegionSettings
        ? {
              ...allRegionSettings,
              city_metadata: allRegionSettings.city_metadata.filter(city =>
                  citySlugs.includes(city.slug)
              ),
          }
        : null;

    return {
        props: {
            regionSettings,
            citySettings,
            validVehicleClasses: await validVehicleClasses(citySlugs),
            validTimeRanges: await validTimeRanges(citySettings),
        },
    };
}

export async function getStaticPaths() {
    const regionSlugs = await api.allRegionalSlugs();

    return {
        paths: regionSlugs.map(regionSlug => `/regions/${regionSlug.slug}`),
        fallback: false,
    };
}

export default Region;
